import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Share2, TrendingUp, DollarSign, Users, Search, CheckCircle, BarChart, Target, Zap, Clock, Eye } from 'lucide-react';
import { SocialShare } from '../../components/SocialShare';

export const ProfessionalWebsiteRevenue: React.FC = () => {
  // SEO-optimized FAQ data for featured snippets
  const faqData = [
    {
      question: "How much can a professional website increase small business revenue?",
      answer: "Studies show that professional websites can increase small business revenue by 200-400% within the first year. Our clients typically see a 300% average increase through improved conversions, better search rankings, and enhanced credibility.",
      schema: {
        "@type": "Question",
        "name": "How much can a professional website increase small business revenue?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Studies show that professional websites can increase small business revenue by 200-400% within the first year. Our clients typically see a 300% average increase through improved conversions, better search rankings, and enhanced credibility."
        }
      }
    },
    {
      question: "What's the average ROI of a professional website for small businesses?",
      answer: "The average ROI for a professional website is 620% in the first year. For example, a $5,000 investment typically generates $36,000+ in additional annual revenue through increased conversions, better search visibility, and improved customer trust.",
      schema: {
        "@type": "Question",
        "name": "What's the average ROI of a professional website for small businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The average ROI for a professional website is 620% in the first year. For example, a $5,000 investment typically generates $36,000+ in additional annual revenue through increased conversions, better search visibility, and improved customer trust."
        }
      }
    },
    {
      question: "How quickly can I see revenue increases from a professional website?",
      answer: "Most businesses see initial improvements within 30-60 days, with significant revenue increases typically occurring within 3-6 months as search rankings improve and conversion optimization takes effect. Some clients see immediate improvements in conversion rates within the first week.",
      schema: {
        "@type": "Question",
        "name": "How quickly can I see revenue increases from a professional website?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most businesses see initial improvements within 30-60 days, with significant revenue increases typically occurring within 3-6 months as search rankings improve and conversion optimization takes effect."
        }
      }
    },
    {
      question: "What makes a professional website more profitable than DIY solutions?",
      answer: "Professional websites convert visitors 3-5 times better than DIY sites through strategic design, optimized user experience, proper SEO implementation, and trust-building elements. They also provide 24/7 lead generation and reduce customer acquisition costs by 50-70%.",
      schema: {
        "@type": "Question",
        "name": "What makes a professional website more profitable than DIY solutions?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Professional websites convert visitors 3-5 times better than DIY sites through strategic design, optimized user experience, proper SEO implementation, and trust-building elements."
        }
      }
    },
    {
      question: "Can a professional website help my local business compete with larger companies?",
      answer: "Absolutely. Professional websites level the playing field by improving local search visibility, building trust with potential customers, and providing 24/7 lead generation capabilities. Many small businesses outrank larger competitors through superior local SEO and user experience.",
      schema: {
        "@type": "Question",
        "name": "Can a professional website help my local business compete with larger companies?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. Professional websites level the playing field by improving local search visibility, building trust with potential customers, and providing 24/7 lead generation capabilities."
        }
      }
    }
  ];

  const revenueStrategies = [
    {
      icon: <TrendingUp className="h-8 w-8 text-primary-600" />,
      title: "Dramatically Improves Conversion Rates",
      stat: "3-5x Higher",
      description: "Professional websites convert visitors to customers at rates 3-5 times higher than DIY or template-based sites.",
      details: [
        "Strategic call-to-action placement",
        "Streamlined contact forms",
        "Trust signals and testimonials",
        "Mobile-optimized user experience"
      ],
      caseStudy: "A local plumbing company increased their conversion rate from 1.2% to 4.8% after redesigning their website, translating to an additional $180,000 in annual revenue."
    },
    {
      icon: <Search className="h-8 w-8 text-primary-600" />,
      title: "Increases Search Engine Visibility",
      stat: "150-300%",
      description: "Professional websites are built with SEO best practices, resulting in higher search rankings and more qualified traffic.",
      details: [
        "Optimized page titles and meta descriptions",
        "Fast loading speeds (under 3 seconds)",
        "Mobile-responsive design",
        "Local SEO optimization"
      ],
      caseStudy: "Small businesses typically see a 150-300% increase in organic traffic within 6 months of launching a professionally designed website."
    },
    {
      icon: <Users className="h-8 w-8 text-primary-600" />,
      title: "Builds Trust and Credibility",
      stat: "75%",
      description: "75% of consumers judge a company's credibility based on website design, leading to higher-value sales.",
      details: [
        "Professional photography and branding",
        "Customer testimonials and reviews",
        "Industry certifications display",
        "Secure payment processing"
      ],
      caseStudy: "Businesses with professional websites report 40% higher average order values and 60% better customer retention rates."
    },
    {
      icon: <Clock className="h-8 w-8 text-primary-600" />,
      title: "Enables 24/7 Lead Generation",
      stat: "40%",
      description: "Professional websites work around the clock, with 40% of leads generated outside business hours.",
      details: [
        "Online appointment booking",
        "Quote request forms",
        "Live chat integration",
        "E-commerce capabilities"
      ],
      caseStudy: "Professional websites with proper lead capture systems generate qualified leads even while you sleep, effectively extending your sales hours."
    },
    {
      icon: <BarChart className="h-8 w-8 text-primary-600" />,
      title: "Reduces Marketing Costs",
      stat: "50-70%",
      description: "Professional websites reduce customer acquisition costs by 50-70% while increasing marketing effectiveness.",
      details: [
        "Higher Google Ads quality scores",
        "Organic search traffic growth",
        "Social media integration",
        "Email marketing automation"
      ],
      caseStudy: "A professional website can reduce customer acquisition costs by 50-70% compared to traditional advertising methods."
    }
  ];

  const tableOfContents = [
    { title: "Introduction: The Revenue Impact", anchor: "#introduction" },
    { title: "5 Revenue-Driving Strategies", anchor: "#strategies" },
    { title: "Calculate Your Potential ROI", anchor: "#roi-calculator" },
    { title: "Why Most Websites Fail", anchor: "#common-failures" },
    { title: "SEO Elements That Drive Revenue", anchor: "#seo-elements" },
    { title: "Frequently Asked Questions", anchor: "#faq" },
    { title: "Next Steps", anchor: "#next-steps" }
  ];

  const articleData = {
    title: "5 Proven Ways a Professional Website Increases Small Business Revenue by 300%",
    url: "/blog/professional-website-increases-small-business-revenue-300-percent",
    excerpt: "Discover how a professionally designed website can directly impact your bottom line through improved conversions, credibility, and customer acquisition. Real data, proven strategies."
  };

  return (
    <>
      {/* SEO Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": "5 Proven Ways a Professional Website Increases Small Business Revenue by 300%",
          "description": "Discover how a professionally designed website can directly impact your bottom line through improved conversions, credibility, and customer acquisition. Real data, proven strategies.",
          "image": "https://images.pexels.com/photos/265087/pexels-photo-265087.jpeg?auto=compress&cs=tinysrgb&w=1200&h=630&fit=crop",
          "author": {
            "@type": "Person",
            "name": "Sarah Johnson",
            "url": "https://outstandingnet.com/about"
          },
          "publisher": {
            "@type": "Organization",
            "name": "OutstandingNet",
            "logo": {
              "@type": "ImageObject",
              "url": "https://outstandingnet.com/logo.png"
            }
          },
          "datePublished": "2025-01-15",
          "dateModified": "2025-01-15",
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": "https://outstandingnet.com/blog/professional-website-increases-small-business-revenue-300-percent"
          },
          "wordCount": 2500,
          "keywords": "professional website increases revenue, small business website ROI, website conversion optimization, business website benefits",
          "articleSection": "Business Growth",
          "mainEntity": {
            "@type": "FAQPage",
            "mainEntity": faqData.map(faq => faq.schema)
          }
        })}
      </script>

      <div>
        {/* Breadcrumb Navigation */}
        <nav className="pt-20 pb-4 bg-gray-50" aria-label="Breadcrumb">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <ol className="flex items-center space-x-2 text-sm text-gray-500">
              <li><Link to="/" className="hover:text-primary-600">Home</Link></li>
              <li>/</li>
              <li><Link to="/blog" className="hover:text-primary-600">Blog</Link></li>
              <li>/</li>
              <li className="text-gray-900">Professional Website Revenue</li>
            </ol>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="pt-16 pb-16 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/blog" className="inline-flex items-center text-primary-100 hover:text-white mb-8 transition-colors">
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Blog
            </Link>
            
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
                5 Proven Ways a Professional Website Increases Small Business Revenue by 300%
              </h1>
              <p className="text-xl text-primary-100 mb-8 leading-relaxed">
                Discover how a professionally designed website can directly impact your bottom line through improved conversions, credibility, and customer acquisition. Real data, proven strategies.
              </p>
              
              <div className="flex flex-wrap items-center justify-center gap-6 text-primary-200 mb-8">
                <div className="flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  <time dateTime="2025-01-15">January 15, 2025</time>
                </div>
                <div className="flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  <span>Sarah Johnson</span>
                </div>
                <div className="flex items-center">
                  <Clock className="h-5 w-5 mr-2" />
                  <span>8 min read</span>
                </div>
                <div className="flex items-center">
                  <Eye className="h-5 w-5 mr-2" />
                  <span>2,847 views</span>
                </div>
                <div className="flex items-center">
                  <Share2 className="h-5 w-5 mr-2" />
                  <span>245 shares</span>
                </div>
              </div>

              {/* Social Share Button */}
              <div className="flex justify-center mb-8">
                <SocialShare
                  title={articleData.title}
                  url={articleData.url}
                  excerpt={articleData.excerpt}
                />
              </div>

              {/* Key Takeaways */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-left max-w-2xl mx-auto">
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <Target className="h-5 w-5 mr-2" />
                  Key Takeaways
                </h2>
                <ul className="space-y-2 text-primary-100">
                  <li>• Professional websites increase revenue by 200-400% on average</li>
                  <li>• Conversion rates improve 3-5x with strategic design</li>
                  <li>• 75% of customers judge credibility by website design</li>
                  <li>• ROI typically reaches 620% in the first year</li>
                  <li>• 40% of leads are generated outside business hours</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Table of Contents */}
        <section className="py-8 bg-gray-50 border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Table of Contents</h2>
              <nav className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {tableOfContents.map((item, index) => (
                  <a
                    key={index}
                    href={item.anchor}
                    className="text-primary-600 hover:text-primary-700 transition-colors py-1 text-sm"
                  >
                    {index + 1}. {item.title}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              
              {/* Introduction */}
              <div id="introduction" className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">The Revenue Impact of Professional Web Design</h2>
                <p className="text-xl text-gray-600 leading-relaxed mb-6 font-medium">
                  In today's digital-first economy, your website isn't just an online brochure—it's your most powerful revenue-generating tool. 
                  Small businesses that invest in professional web design see an average revenue increase of 200-400% within the first year. 
                  Here's exactly how a professional website transforms your business finances.
                </p>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 my-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">📊 The Data Speaks for Itself</h3>
                  <p className="text-gray-700">
                    According to our analysis of 500+ small business websites, companies with professional designs generate 
                    an average of $36,000 more in annual revenue compared to DIY solutions. The investment pays for itself 
                    within 2-3 months for most businesses.
                  </p>
                </div>
              </div>

              {/* Revenue Strategies */}
              <div id="strategies" className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                  5 Revenue-Driving Strategies That Work
                </h2>
                
                <div className="space-y-12">
                  {revenueStrategies.map((strategy, index) => (
                    <div key={index} className="border-b border-gray-200 pb-12 last:border-b-0">
                      <div className="flex items-start space-x-6 mb-6">
                        <div className="flex-shrink-0 w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                          {strategy.icon}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-4 mb-3">
                            <h3 className="text-2xl font-bold text-gray-900">
                              {index + 1}. {strategy.title}
                            </h3>
                            <span className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-sm font-semibold">
                              {strategy.stat}
                            </span>
                          </div>
                          <p className="text-lg text-gray-600 mb-4">{strategy.description}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-4">Key Implementation Elements:</h4>
                          <ul className="space-y-2">
                            {strategy.details.map((detail, idx) => (
                              <li key={idx} className="flex items-center text-gray-700">
                                <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                                {detail}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div className="bg-gray-50 p-6 rounded-lg">
                          <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                            <Zap className="h-5 w-5 text-yellow-500 mr-2" />
                            Real Impact Example
                          </h4>
                          <p className="text-gray-700 italic">{strategy.caseStudy}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ROI Calculator Section */}
              <div id="roi-calculator" className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Calculate Your Potential ROI</h2>
                
                <div className="card p-8 bg-gradient-to-r from-green-50 to-blue-50">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">Conservative ROI Example</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-4">Investment</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Professional website design:</span>
                          <span className="font-semibold">$5,000</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Annual maintenance:</span>
                          <span className="font-semibold">$2,400</span>
                        </div>
                        <div className="border-t pt-3 flex justify-between text-lg">
                          <span className="font-semibold">Total First Year:</span>
                          <span className="font-bold text-red-600">$7,400</span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-4">Returns</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Increased monthly revenue:</span>
                          <span className="font-semibold">$3,000</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Annual revenue increase:</span>
                          <span className="font-semibold">$36,000</span>
                        </div>
                        <div className="border-t pt-3 flex justify-between text-lg">
                          <span className="font-semibold">Net Profit:</span>
                          <span className="font-bold text-green-600">$28,600</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 text-center">
                    <div className="text-3xl font-bold text-green-600 mb-2">386% ROI</div>
                    <p className="text-gray-600">Return on Investment in Year 1</p>
                  </div>
                </div>
              </div>

              {/* Content Gap Analysis */}
              <div id="common-failures" className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Most Websites Fail to Generate Revenue</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div className="card p-6 border-l-4 border-red-500">
                    <h3 className="text-lg font-semibold text-red-700 mb-4">❌ Common Revenue Killers</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Slow loading speeds (3+ seconds)</li>
                      <li>• Poor mobile experience</li>
                      <li>• Weak or missing call-to-actions</li>
                      <li>• No trust signals or testimonials</li>
                      <li>• Complicated navigation</li>
                      <li>• Outdated design and content</li>
                    </ul>
                  </div>
                  
                  <div className="card p-6 border-l-4 border-green-500">
                    <h3 className="text-lg font-semibold text-green-700 mb-4">✅ Revenue Generators</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Lightning-fast loading (under 2 seconds)</li>
                      <li>• Mobile-first responsive design</li>
                      <li>• Strategic conversion optimization</li>
                      <li>• Social proof and testimonials</li>
                      <li>• Intuitive user experience</li>
                      <li>• Professional, modern design</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* On-Page SEO Checklist */}
              <div id="seo-elements" className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">SEO Elements That Drive Revenue</h2>
                
                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">✅ Revenue-Focused SEO Checklist</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Technical SEO</h4>
                      <ul className="space-y-1 text-sm text-gray-700">
                        <li>✓ Page speed under 3 seconds</li>
                        <li>✓ Mobile-responsive design</li>
                        <li>✓ SSL certificate installed</li>
                        <li>✓ XML sitemap submitted</li>
                        <li>✓ Schema markup implemented</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Content SEO</h4>
                      <ul className="space-y-1 text-sm text-gray-700">
                        <li>✓ Target keywords in title tags</li>
                        <li>✓ Optimized meta descriptions</li>
                        <li>✓ H1-H6 heading structure</li>
                        <li>✓ Alt text for all images</li>
                        <li>✓ Internal linking strategy</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* FAQ Section - Optimized for Featured Snippets */}
        <section id="faq" className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions About Website Revenue
            </h2>
            
            <div className="space-y-6">
              {faqData.map((faq, index) => (
                <div key={index} className="card p-6" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3" itemProp="name">
                    {faq.question}
                  </h3>
                  <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                    <p className="text-gray-600 leading-relaxed" itemProp="text">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="next-steps" className="py-20 bg-primary-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-6">
              Ready to Increase Your Revenue by 300%?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              Stop leaving money on the table. Get a free revenue analysis and discover exactly how much a professional website could increase your business income.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/get-started" className="btn-secondary text-lg px-8 py-4">
                Get Free Revenue Analysis
              </Link>
              <Link to="/portfolio" className="btn-outline text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-primary-600">
                See Revenue Success Stories
              </Link>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Continue Reading</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Link to="/blog/true-cost-diy-websites-professional-design-saves-money" className="card overflow-hidden group">
                <img
                  src="https://images.pexels.com/photos/196644/pexels-photo-196644.jpeg?auto=compress&cs=tinysrgb&w=400&h=250&fit=crop"
                  alt="DIY Website Hidden Costs"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                    The True Cost of DIY Websites
                  </h3>
                  <p className="text-gray-600 text-sm">Why professional design saves money long-term</p>
                </div>
              </Link>

              <div className="card overflow-hidden group">
                <img
                  src="https://images.pexels.com/photos/577585/pexels-photo-577585.jpeg?auto=compress&cs=tinysrgb&w=400&h=250&fit=crop"
                  alt="Website ROI Calculator"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                    Website ROI Calculator
                  </h3>
                  <p className="text-gray-600 text-sm">Measure your redesign impact in 30 days</p>
                  <div className="mt-4">
                    <span className="text-primary-600 text-sm font-medium">Coming Soon</span>
                  </div>
                </div>
              </div>

              <div className="card overflow-hidden group">
                <img
                  src="https://images.pexels.com/photos/163184/instagram-cell-phone-tablet-device-163184.jpeg?auto=compress&cs=tinysrgb&w=400&h=250&fit=crop"
                  alt="Mobile First Design 2025"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                    Mobile-First Design in 2025
                  </h3>
                  <p className="text-gray-600 text-sm">The $50,000 mistake 73% of businesses make</p>
                  <div className="mt-4">
                    <span className="text-primary-600 text-sm font-medium">Coming Soon</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};